import { useState, useRef, useEffect } from 'react';

import styled from '@emotion/styled';
import { colors } from '@modernanimal/common';
import UseOutsideAlerter from '@modernanimal/common/src/web/atoms/OutsideAlerter';

import InputField from './InputField';
import Fonts from '../fonts';
import Icons from '../icons';

type Props<T> = {
    placeholder: string;
    selectedValue: T | null;
    // Can potentially be undefined if searchQuery is debounced
    searchQuery: (searchValue: string)=> Promise<T[]> | T[] | undefined;
    onSelectResult: (result: T)=> void;
    searchResultKey: keyof T | ((result: T)=> string);
    searchResultName: keyof T;
    autoFocus?: boolean;
    selectOnEnter?: boolean;
    clearOnSelect?: boolean;
    otherSearchResult?: T; // Represents the "Other" result if no search results are found
    isSearchValueValid?: boolean;
    setIsSearchValueValid?: (isSearchValueValid: boolean)=> void;
    disabled?: boolean;
};

const SearchInputField = <T extends object>({
    placeholder, selectedValue, searchQuery, onSelectResult, searchResultKey, searchResultName,
    autoFocus, selectOnEnter, clearOnSelect, otherSearchResult, isSearchValueValid = true, setIsSearchValueValid,
    disabled = false
}: Props<T>) => {
    const [searchValue, setSearchValue] = useState('');
    const [searchResults, setSearchResults] = useState<T[]>([]);
    const [showResults, setShowResults] = useState(false);

    const searchResultsRef = useRef<HTMLDivElement>(null);
    UseOutsideAlerter([searchResultsRef], () => {
        /*
        The reason the validation logic is split between the outside click alerter and the onBlur event handler
        is because if everything was in onBlur, the validation would trigger even if clicking on a search result.
        The outside click alerter mimicks the functionality of onBlur of the input field. The onBlur handles an
        edge case where a user focuses on the search input, doesn't type anything, then blurs the input; the outside
        click alerter can't handle this case because it's only triggered if the results are showing.
        */
        if (!showResults) return;

        if (selectedValue && selectedValue[searchResultName]) {
            setSearchValue(selectedValue[searchResultName] as string);
        } else if (setIsSearchValueValid) {
            setIsSearchValueValid(false);
        }
        setShowResults(false);
    });

    useEffect(() => {
        setSearchValue(selectedValue ? selectedValue[searchResultName] as string : '');
    }, [selectedValue, searchResultName]);

    const search = async (searchValue: string) => {
        setSearchValue(searchValue);
        const searchResults = await searchQuery(searchValue);
        if (!searchResults) return;

        setSearchResults(searchResults);
        setShowResults((!!selectedValue && !!selectedValue[searchResultName]) || searchValue !== '');
    };

    const selectResult = (result: T) => {
        onSelectResult(result);
        setSearchValue(clearOnSelect ? '' : result[searchResultName] as string);
        setShowResults(false);

        if (setIsSearchValueValid) setIsSearchValueValid(true);
    };

    const validateSearchValue = () => {
        if (!selectedValue) return;

        if (!selectedValue[searchResultName] && searchValue === '' && setIsSearchValueValid) {
            setIsSearchValueValid(false);
        }
    };

    const renderEmptyResults = () => (
        <>
            <SearchResult key="no-result" isSelectable={false}>
                <Icons.Info height={16} width={16} fill={colors.RED} />
                <Fonts.Body>
                    {`No results found. Please try again${otherSearchResult ? ' or select other' : ''}.`}
                </Fonts.Body>
            </SearchResult>
            {otherSearchResult && (
                <SearchResult
                    key="other-result"
                    onClick={() => selectResult(otherSearchResult)}
                    isSelectable
                >
                    <Fonts.Body>{otherSearchResult[searchResultName] as string}</Fonts.Body>
                </SearchResult>
            )}
        </>
    );

    return (
        <SearchInputFieldContainer>
            <InputField
                placeholder={placeholder}
                value={searchValue}
                onChange={search}
                onKeyDown={(e) => {
                    if (selectOnEnter && e.key === 'Enter') {
                        selectResult({ name: searchValue } as T);
                    }
                }}
                autoFocus={autoFocus}
                hasRightIcon
                isInvalid={!isSearchValueValid}
                onBlur={validateSearchValue}
                disabled={disabled}
            />
            <SearchIconContainer>
                <Icons.Search width={24} height={24} fill={colors.PRIMARY_PERIWINKLE} />
            </SearchIconContainer>
            {showResults && (
                <SearchResultsContainer ref={searchResultsRef}>
                    {searchResults.length === 0 && renderEmptyResults()}
                    {searchResults.map((searchResult) => (
                        <SearchResult
                            key={typeof searchResultKey === 'function' ? searchResultKey(searchResult) : searchResult[searchResultKey] as string}
                            onClick={() => selectResult(searchResult)}
                            isSelectable
                        >
                            <Fonts.Body>{searchResult[searchResultName] as string}</Fonts.Body>
                        </SearchResult>
                    ))}
                </SearchResultsContainer>
            )}
        </SearchInputFieldContainer>
    );
};

export default SearchInputField;

const SearchInputFieldContainer = styled.div`
    display: flex;
    position: relative;
    align-items: center;
`;

const SearchIconContainer = styled.div`
    display: flex;
    position: absolute;
    right: 12px;
`;

const SearchResult = styled.button<{ isSelectable: boolean }>`
    display: flex;
    flex-direction: row;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    align-items: flex-start;
    text-align: start;

    ${({ isSelectable }) => (isSelectable ? `
        &:hover {
            background-color: ${colors.GREY_7};
        }

        &:focus {
            outline: none!important;
            background-color: ${colors.GREY_7};
        };
    ` : `
        cursor: auto;
    `)}
`;

const SearchResultsContainer = styled.div`
    display: flex;
    flex-direction: column;
    position: absolute;
    left: 0px;
    top: 58px; // InputField height is 48px + 10px gap
    width: 100%;
    max-height: 216px;
    border-radius: 8px;
    overflow-y: scroll;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.25);
    padding: 8px 0px;
    background-color: ${colors.WHITE};
    z-index: 1;
`;
